import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { motion } from 'framer-motion'
import { ExclamationTriangleIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline'

interface ProtectedAdminRouteProps {
  children: ReactNode
  requireSuperAdmin?: boolean
}

const ProtectedAdminRoute = ({ children, requireSuperAdmin = false }: ProtectedAdminRouteProps) => {
  const { user, isLoading, isAdmin, isSuperAdmin } = useAuth()

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Checking permissions...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/auth" replace />
  }

  // Check admin permissions
  const hasRequiredPermissions = requireSuperAdmin ? isSuperAdmin() : isAdmin()

  // Show access denied if user doesn't have required permissions
  if (!hasRequiredPermissions) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Background orbs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
          <div className="floating-orb floating-orb-4"></div>
          <div className="floating-orb floating-orb-5"></div>
        </div>

        <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full text-center"
          >
            <div className="glass-mockup rounded-2xl p-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="w-16 h-16 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center"
              >
                <ShieldExclamationIcon className="w-8 h-8 text-red-400" />
              </motion.div>
              
              <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
              <p className="text-gray-400 mb-6">
                {requireSuperAdmin 
                  ? 'You need super admin privileges to access this page.'
                  : 'You need admin privileges to access this page.'
                }
              </p>
              
              <div className="space-y-3">
                <motion.button
                  onClick={() => window.history.back()}
                  className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl font-medium transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Go Back
                </motion.button>
                
                <motion.button
                  onClick={() => window.location.href = '/dashboard'}
                  className="w-full px-6 py-3 text-gray-400 hover:text-white transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Return to Dashboard
                </motion.button>
              </div>
              
              <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <div className="flex items-center space-x-2 text-yellow-400">
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">Need Admin Access?</span>
                </div>
                <p className="text-yellow-300 text-xs mt-1">
                  Contact your system administrator to request admin privileges.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  // User has required permissions, render the protected content
  return <>{children}</>
}

export default ProtectedAdminRoute 